/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import type { Filters, Listable, Property } from '@zcode/state-type'
import { dollarize, isTruthy } from '@zcode/utils'

import { HOOD_SEARCH, NEARBY_SEARCH } from '../../constants/index'

import type { WeirdPartialListable } from './createSearchQuery'

/**
 * Creates a name for a search
 *
 * @param root0
 * @param root0.entity
 * @param root0.filters
 * @param root0.type
 */
export function createSearchName({
  type,
  entity,
  filters,
}: {
  entity: WeirdPartialListable | Listable | Property | null | undefined
  filters: Partial<Filters> | undefined
  type: typeof HOOD_SEARCH | typeof NEARBY_SEARCH
}) {
  if (!entity && !filters) {
    return ''
  }
  if (entity) {
    if (type === HOOD_SEARCH && entity.neighborhoodName) {
      return [entity.neighborhoodName, entity.neighborhood?.city || entity.city]
        .filter(isTruthy)
        .join(', ')
    }
    if (type === NEARBY_SEARCH && entity.address) {
      // remove 'apartments' from title to give cleaner search names
      return `Near ${entity.address} for Rent` as const
    }
  }
  // @ts-expect-error hood and city don't exist in filters type -- maybe just delete these 3 lines?
  if (filters?.hood || filters?.city) {
    // @ts-expect-error hood and city don't exist in filters
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `Near ${filters.hood || filters.city} for Rent` as const
  }
  if (filters?.minPrice && filters?.maxPrice) {
    return `Price between ${dollarize(filters.minPrice)} - ${dollarize(
      filters.maxPrice
    )} for Rent` as const
  }
  if (filters?.minPrice) {
    return `Price greater than ${dollarize(filters.minPrice)} for Rent` as const
  }
  if (filters?.maxPrice) {
    return `Price less than ${dollarize(filters.maxPrice)} for Rent` as const
  }
  if (filters?.cats && filters.dogs) {
    return 'Pet friendly Apartments for Rent'
  }
  if (filters?.cats) {
    return 'Cat friendly Apartments for Rent'
  }
  if (filters?.dogs) {
    return 'Dog friendly Apartments for Rent'
  }
  return 'Apartments for Rent'
}
